import { faColumns, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Spinner, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ColumnVisibilityToggle from 'components/common/ColumnVisibilityToggle';
import FilterTab, { FilterTabItem } from 'components/common/FilterTab';
import RefundsTable, { refundsTableColumns, createRefundsTableColumns } from 'components/tables/RefundsTable';
import useAdvanceTable from 'hooks/products/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { PaginationState, VisibilityState } from '@tanstack/react-table';
import { FilterMenu } from 'components/common/FilterButtonGroup';
import RefundFilters from 'components/refunds/RefundFilters';
import { useRefundsData } from 'hooks/refunds/useRefundsData';

// Import SCSS for animations
import 'assets/scss/orders/animations.scss';

/**
 * Refunds Component - Displays a list of refunds with filtering and sorting
 */
const Refunds = () => {
  // Initialize translation
  const { t } = useTranslation();
  // State for column visibility
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    id: true,
    customer_name: true,
    customer_email: true,
    order_ref: true,
    resolution_type: true,
    refunded_amount: true,
    status: true,
    created_at: true
  });
  
  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState("");
  
  // Filter state
  const [activeStatus, setActiveStatus] = useState<number | null>(null);
  const [activeStatusLabel, setActiveStatusLabel] = useState<string>(t('refunds.status'));
  const [activeResolution, setActiveResolution] = useState<number | null>(null);
  const [activeResolutionLabel, setActiveResolutionLabel] = useState<string>(t('refunds.resolution'));
  
  // Update labels when language changes
  useEffect(() => {
    if (activeStatus === null) {
      setActiveStatusLabel(t('refunds.status'));
    } else {
      const statusKey = `refunds.statusTypes.${
        activeStatus === 0 ? 'pending' : 
        activeStatus === 1 ? 'processing' :
        activeStatus === 2 ? 'resolved' :
        activeStatus === 3 ? 'declined' :
        activeStatus === 4 ? 'canceled' : 'unknown'
      }`;
      setActiveStatusLabel(`${t('refunds.status')}: ${t(statusKey)}`);
    }
    
    if (activeResolution === null) {
      setActiveResolutionLabel(t('refunds.resolution'));
    } else {
      const resolutionKey = `refunds.resolutionTypes.${
        activeResolution === 1 ? 'refund' :
        activeResolution === 2 ? 'replace' :
        activeResolution === 3 ? 'cancel' : 'unknown'
      }`;
      setActiveResolutionLabel(`${t('refunds.resolution')}: ${t(resolutionKey)}`);
    }
  }, [t, activeStatus, activeResolution]);
  
  // Memoize the filter options to prevent unnecessary rerenders
  const filterOptions = useMemo(() => ({
    search: submittedSearchTerm,
    status: activeStatus,
    resolution_type: activeResolution
  }), [submittedSearchTerm, activeStatus, activeResolution]);

  // Memoize the pagination options
  const paginationOptions = useMemo(() => ({
    initialPageIndex: 0,
    initialPageSize: 10
  }), []);

  // Use our custom hook for refunds data
  const { 
    tableData, 
    loading, 
    paginationCount,
    pagination, 
    handlePageChange, 
    handlePageSizeChange, 
    fetchRefunds,
    statusCounts,
    calculateSumAll
  } = useRefundsData(filterOptions, paginationOptions);
  
  // Handler for tab clicks
  const handleTabClick = useCallback((tabValue: string) => {
    if (tabValue === '0') {
      setStatusFilter(0, `${t('refunds.status')}: ${t('refunds.statusTypes.pending')}`);
    } else if (tabValue === '1') {
      setStatusFilter(1, `${t('refunds.status')}: ${t('refunds.statusTypes.processing')}`);
    } else if (tabValue === '2') {
      setStatusFilter(2, `${t('refunds.status')}: ${t('refunds.statusTypes.resolved')}`);
    } else if (tabValue === '3') {
      setStatusFilter(3, `${t('refunds.status')}: ${t('refunds.statusTypes.declined')}`);
    } else if (tabValue === '4') {
      setStatusFilter(4, `${t('refunds.status')}: ${t('refunds.statusTypes.canceled')}`);
    } else {
      // Reset status filter for "All" tab
      setStatusFilter(null, t('refunds.status'));
    }
  }, [t]);
  
  // Create tab items with proper translations
  const createTabItems = useCallback(() => {
    return [
      {
        label: t('all'),
        value: 'all',
        count: 0, // Will be updated dynamically
        onClick: () => handleTabClick('all')
      },
      {
        label: t('refunds.statusTypes.pending'),
        value: '0',
        count: 0,
        onClick: () => handleTabClick('0')
      },
      {
        label: t('refunds.statusTypes.processing'),
        value: '1',
        count: 0,
        onClick: () => handleTabClick('1')
      },
      {
        label: t('refunds.statusTypes.resolved'),
        value: '2',
        count: 0,
        onClick: () => handleTabClick('2')
      },
      {
        label: t('refunds.statusTypes.declined'),
        value: '3',
        count: 0,
        onClick: () => handleTabClick('3')
      },
      {
        label: t('refunds.statusTypes.canceled'),
        value: '4',
        count: 0,
        onClick: () => handleTabClick('4')
      }
    ];
  }, [t, handleTabClick]);
  
  // State for tab items
  const [tabItems, setTabItems] = useState<FilterTabItem[]>(createTabItems());
  
  // Update tab items when language changes
  useEffect(() => {
    setTabItems(prevItems => {
      const newItems = createTabItems();
      
      // Preserve counts from previous items
      return newItems.map((item, index) => ({
        ...item,
        count: prevItems[index]?.count || 0
      }));
    });
  }, [t, createTabItems]);
  
  // Interface for column options
  interface ColumnOption {
    id: string;
    label: string;
    visible: boolean;
  }
  
  // Convert columns to options for the column toggler
  const columnOptions = useMemo<ColumnOption[]>(() => {
    return refundsTableColumns.map(column => {
      const columnId = typeof column.accessorKey === 'string' 
        ? column.accessorKey 
        : column.id as string;
      
      return {
        id: columnId,
        label: column.header as string,
        visible: columnVisibility[columnId] !== false
      };
    });
  }, [columnVisibility]);
  
  // Handler for toggling column visibility
  const handleToggleColumn = (columnId: string, visible: boolean) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnId]: visible
    }));
  };
  
  // Status filter handler
  const setStatusFilter = (status: number | null, label: string) => {
    setActiveStatus(status);
    setActiveStatusLabel(label);
    handlePageChange(0); // Reset to first page
  };
  
  // Create translated columns using the t function
  const translatedColumns = useMemo(() => createRefundsTableColumns(t), [t]);
  
  // Set up the table with filtered columns based on visibility
  const table = useAdvanceTable({
    data: tableData,
    columns: translatedColumns.filter(column => {
      const columnId = typeof column.accessorKey === 'string' 
        ? column.accessorKey 
        : column.id as string;
      return columnVisibility[columnId] !== false;
    }),
    pageSize: pagination.pageSize,
    pagination: true,
    sortable: true,
    rowCount: paginationCount.totalCount,
    manualPagination: true,
    onPaginationChange: (newPagination: { pageIndex: number; pageSize: number }) => {
      // If only the page index changed
      if (newPagination.pageSize === pagination.pageSize) {
        handlePageChange(newPagination.pageIndex);
      } else {
        // If the page size changed
        handlePageSizeChange(newPagination as PaginationState);
      }
    }
  });
  
  // Update tab items counts based on status counts
  useEffect(() => {
    if (tabItems && tabItems.length > 0) {
      setTabItems(prev => {
        const updated = [...prev];
        
        // Set the total count for the "All" tab
        updated[0] = { ...updated[0], count: calculateSumAll() };
        
        // Update counts for each status tab
        if (statusCounts && statusCounts.length > 0) {
          statusCounts.forEach((statusCount: { code: number; count: number }) => {
            const tabIndex = updated.findIndex(tab => tab.value === statusCount.code.toString());
            if (tabIndex > 0) { // Skip "All" tab
              updated[tabIndex] = { ...updated[tabIndex], count: statusCount.count };
            }
          });
        }
        
        return updated;
      });
    }
  }, [statusCounts, calculateSumAll]);
  
  // Initial fetch on component mount - run only once
  const initialFetchRef = useRef(false);
  useEffect(() => {
    if (!initialFetchRef.current) {
      fetchRefunds();
      initialFetchRef.current = true;
    }
  }, [fetchRefunds]);
  
  // Use ref to track previous filter values for comparison
  const prevFilterRef = useRef({
    activeStatus,
    submittedSearchTerm,
    activeResolution
  });
  
  // Refetch data when filters change
  useEffect(() => {
    // Check if any values have changed
    const currentFilters = {
      activeStatus,
      submittedSearchTerm,
      activeResolution
    };
    
    // Compare with previous values
    const hasChanged = Object.keys(currentFilters).some(
      key => currentFilters[key as keyof typeof currentFilters] !== 
             prevFilterRef.current[key as keyof typeof prevFilterRef.current]
    );
    
    if (hasChanged) {
      // Reset to first page
      handlePageChange(0);
      table.setPageIndex(0);
      // Fetch refunds with new filters
      fetchRefunds();
      
      // Update refs with current values
      prevFilterRef.current = currentFilters;
    }
  }, [
    activeStatus, 
    submittedSearchTerm,
    activeResolution,
    fetchRefunds,
    handlePageChange,
    table
  ]);
  
  // Handle search input changes
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle search on Enter key
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSubmittedSearchTerm(searchTerm);
    }
  };

  // Clear search handler
  const handleClearSearch = () => {
    setSearchTerm("");
    setSubmittedSearchTerm("");
  };
  
  // Resolution filter handler
  const setResolutionFilter = (resolution: number | null, label: string) => {
    setActiveResolution(resolution);
    setActiveResolutionLabel(label);
    handlePageChange(0); // Reset to first page
  };

  return (
    <div>
      <div className="mb-9">
        <h2 className="mb-4">{t('refunds.title')}</h2>
        <FilterTab tabItems={tabItems} className="mb-2" />

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap align-items-start justify-content-between mb-3">
              <div className="flex-grow-1">
                {/* Refund Filters Component */}
                <RefundFilters 
                  searchTerm={searchTerm}
                  onSearchChange={handleSearchChange}
                  onSearchKeyPress={handleSearchKeyPress}
                  onClearSearch={handleClearSearch}
                  filterMenus={[]}
                  
                  // Add the customize button inline with filters
                  customizeButton={
                    <div className="d-flex">
                      {/* Resolution filter dropdown */}
                      <Dropdown className="me-2">
                        <Dropdown.Toggle
                          variant="phoenix-secondary"
                          className="px-7 flex-shrink-0 dropdown-caret-none"
                        >
                          {activeResolutionLabel}
                          <FontAwesomeIcon
                            icon={faAngleDown}
                            className="ms-2"
                            transform="down-2"
                          />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => setResolutionFilter(null, t('refunds.resolution'))}>
                            {t('refunds.allResolutions')}
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setResolutionFilter(1, `${t('refunds.resolution')}: ${t('refunds.resolutionTypes.refund')}`)}>
                            {t('refunds.resolutionTypes.refund')}
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => setResolutionFilter(2, `${t('refunds.resolution')}: ${t('refunds.resolutionTypes.replace')}`)}>
                            {t('refunds.resolutionTypes.replace')}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      
                      {/* Customize table dropdown */}
                      <Dropdown>
                        <Dropdown.Toggle
                          variant="outline-warning"
                          className="px-7 flex-shrink-0 dropdown-caret-none"
                          style={{
                            backgroundColor: 'transparent',
                            border: '2px solid #ff7f00',
                            color: '#ff7f00',
                            borderRadius: '8px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                          aria-label={t('customizeTableColumns')}
                        >
                          <FontAwesomeIcon icon={faColumns} className="me-2" />
                          {t('customizeTable')}
                          <FontAwesomeIcon
                            icon={faAngleDown}
                            className="ms-2"
                            transform="down-2"
                          />
                        </Dropdown.Toggle>
                        <Dropdown.Menu
                          style={{
                            padding: '12px',
                            borderRadius: '10px',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                            minWidth: '200px',
                          }}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          {columnOptions.map((column) => (
                            <ColumnVisibilityToggle
                              key={column.id}
                              id={column.id}
                              label={column.label}
                              checked={column.visible}
                              onChange={(checked) => handleToggleColumn(column.id, checked)}
                            />
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  }
                />
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <Spinner animation="border" role="status" variant="warning">
                  <span className="visually-hidden">{t('loading')}</span>
                </Spinner>
              </div>  
            ) : (
              <RefundsTable totalCount={paginationCount.totalCount} />
            )}
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Refunds;
